/** Relative mode of the TimeWindowPanel: "last N" (and, for forward-looking fields,
 *  "next N") windows. A preset click applies at once; the custom row takes any amount.
 *  The window stays relative on the wire unless the consumer freezes it. */
import UpdateIcon from '@mui/icons-material/Update';
import {
  Box,
  Button,
  Divider,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';

import { TIME_WINDOW_PRESETS } from './presets';
import { MAX_RELATIVE_AMOUNT } from './relative';
import type { TimeWindowUnit } from './types';

export type RelativeDirection = 'last' | 'next';

/** A preset-shaped window: [now − amount·unit, now] or [now, now + amount·unit]. */
export interface RelativeWindow {
  direction: RelativeDirection;
  amount: number;
  unit: TimeWindowUnit;
}

const UNIT_OPTIONS: { unit: TimeWindowUnit; one: string; many: string }[] = [
  { unit: 'minutes', one: 'minute', many: 'minutes' },
  { unit: 'hours', one: 'hour', many: 'hours' },
  { unit: 'days', one: 'day', many: 'days' },
  { unit: 'weeks', one: 'week', many: 'weeks' },
  { unit: 'months', one: 'month', many: 'months' },
];

const DIRECTION_WORD: Record<RelativeDirection, string> = { last: 'Last', next: 'Next' };

/** "Last 15 minutes" / "Next 1 hour" — preset buttons' accessible names. */
export function relativeWindowName(w: RelativeWindow): string {
  const unit = UNIT_OPTIONS.find((o) => o.unit === w.unit)!;
  return `${DIRECTION_WORD[w.direction]} ${w.amount} ${w.amount === 1 ? unit.one : unit.many}`;
}

interface Props {
  /** The window currently applied (highlighted), if it is relative. */
  selected: RelativeWindow | null;
  allowFuture: boolean;
  /** Whether the result stays relative (live) — only changes the footnote. */
  live: boolean;
  onApply: (w: RelativeWindow) => void;
}

export function RelativePane({ selected, allowFuture, live, onApply }: Props) {
  const [direction, setDirection] = useState<RelativeDirection>(
    allowFuture && selected ? selected.direction : 'last',
  );
  const [amount, setAmount] = useState(selected ? String(selected.amount) : '');
  const [unit, setUnit] = useState<TimeWindowUnit>(selected?.unit ?? 'minutes');

  const parsed = /^\d+$/.test(amount) ? Number(amount) : NaN;
  const customValid = parsed >= 1 && parsed <= MAX_RELATIVE_AMOUNT;
  const applyCustom = () => {
    if (customValid) onApply({ direction, amount: parsed, unit });
  };

  return (
    <Box>
      {allowFuture && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={direction}
          onChange={(_, v: RelativeDirection | null) => v && setDirection(v)}
          aria-label="Direction"
          sx={{ mb: 1.25 }}
        >
          <ToggleButton value="last" sx={{ px: 1.25, py: 0.25 }}>
            Last
          </ToggleButton>
          <ToggleButton value="next" sx={{ px: 1.25, py: 0.25 }}>
            Next
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '64px 1fr',
          columnGap: 1.5,
          rowGap: 0.75,
          alignItems: 'center',
        }}
      >
        {TIME_WINDOW_PRESETS.map((group) => (
          <Fragment key={group.unit}>
            <Typography variant="caption" color="text.secondary">
              {group.label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {group.amounts.map((n) => {
                const w: RelativeWindow = { direction, amount: n, unit: group.unit };
                const active =
                  selected !== null &&
                  selected.direction === direction &&
                  selected.unit === group.unit &&
                  selected.amount === n;
                const name = relativeWindowName(w);
                return (
                  <Button
                    key={n}
                    size="small"
                    variant={active ? 'contained' : 'outlined'}
                    aria-label={name}
                    aria-pressed={active}
                    title={name}
                    onClick={() => onApply(w)}
                    sx={{
                      minWidth: 36,
                      px: 1,
                      py: 0.1,
                      fontVariantNumeric: 'tabular-nums',
                      // Outlined at rest reads as a hairline, not a row of buttons.
                      ...(active ? {} : { borderColor: 'divider', color: 'text.primary' }),
                    }}
                  >
                    {n}
                  </Button>
                );
              })}
            </Box>
          </Fragment>
        ))}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          applyCustom();
        }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Typography variant="body2" sx={{ width: 64, flexShrink: 0, color: 'text.secondary' }}>
          {DIRECTION_WORD[direction]}
        </Typography>
        <TextField
          size="small"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/\D/g, '').slice(0, 4))}
          placeholder="N"
          inputProps={{
            inputMode: 'numeric',
            'aria-label': `${DIRECTION_WORD[direction]} how many`,
            style: { fontVariantNumeric: 'tabular-nums' },
          }}
          sx={{ width: 76 }}
        />
        <TextField
          select
          size="small"
          value={unit}
          onChange={(e) => setUnit(e.target.value as TimeWindowUnit)}
          inputProps={{ 'aria-label': 'Unit' }}
          sx={{ width: 120 }}
        >
          {UNIT_OPTIONS.map((o) => (
            <MenuItem key={o.unit} value={o.unit}>
              {parsed === 1 ? o.one : o.many}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button type="submit" variant="contained" size="small" disabled={!customValid}>
          Apply
        </Button>
      </Box>

      {live && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.25 }}
        >
          <UpdateIcon sx={{ fontSize: 16 }} aria-hidden />
          Stays relative — moves with every refresh
        </Typography>
      )}
    </Box>
  );
}
